// ----------------------------------------------------------------------
// <copyright file="Izenda.GridLayoutUIStore.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------
!(function() {
  var extendObservable = require('mobx').extendObservable;
  var toJS = require('mobx').toJS;
  var action = require('mobx').action;
  var transaction = require('mobx').transaction;

  var layoutComputerUtils = require('./Izenda.LayoutComputerUtils');
  var GridItemState = require('./Izenda.GridItemState');
  var SnapUtils = require('./Izenda.GridViewSnapUtils');
  const { sortBy,find, isNil, filter, some, assign, chain,placeholder } = require('lodash/fp');
  const UIConstants = require('logic/data/Izenda.UIConstants')
  // Determine intersection area ratio for swapping
  var SWAP_INTERSECT_RATIO = 0.33;
  // Determine intersection ratio for snapping
  var SNAP_RATIO = 0.5;
  // Determine additional grid columns in case of having infinity columns
  var DEFAULT_ADD_COLS = 1;
  // Determine additional grid rows in case of having infinity rows
  var DEFAULT_ADD_ROWS = 1;

  function GridLayoutUIStore(
    cellWidth,
    cellHeight,
    width,
    rows,
    cols,
    infinityRows,
    infinityCols,
    isFullSize,
    allowSwap,
    minItemWidth,
    minItemHeight,
    maxHeight
  ) {
    this.rows = rows;
    this.cols = cols;
    this.infinityRows = infinityRows;
    this.infinityCols = infinityCols;
    this.allowSwap = allowSwap;
    this.minItemHeight = minItemHeight || 1;
    this.minItemWidth = minItemWidth || 1;
    this.maxHeight = maxHeight;
    this.resize = false;
    this.leftPadding = 0;

    extendObservable(this, {
      // Original width px set by outside.
      innerWidth: width,
      // Grid cell width in pixel
      cellWidth: cellWidth,
      // Grid cell height in pixel
      cellHeight: cellHeight,
      // Layout item that is interacting
      overlayItem: function() {
        return find('isInteracting', this.layout);
      },
      // Current position of interacting overlay box
      overlayPosition: {
        top: 0,
        left: 0,
        width: 0,
        height: 0
      },
      // Snap editing mode
      isSnapEditOn: false,
      // Temp layouts for updating after user stop interacting
      compactedLayout: [],
      // Grid layout full size
      isFullSize: isFullSize,
      // Full height only
      isFullHeightOnly: false,
      // List of layout item
      layout: [],
      // Actual view width
      width: function() {
        // Only round width value by multiply of cell width in front-side mode.
        // In back-side mode (showing a full size item), its width must be the same
        // with container width. Because the back side UI of report part must fit
        // the size of container.
        return this.fullSizedItem || this.isFullSize
          ? this.innerWidth
          : layoutComputerUtils.roundWidth(this.innerWidth, this.cellWidth);
      },
      // Actual view height
      height: function() {
        return !isNil(this.maxHeight) && this.hasFullSizeItem ? this.maxHeight : this.calcHeight();
      },
      gridWidth: function() {
        return layoutComputerUtils.getGridWidth(this.width, this.cellWidth);
      },
      gridHeight: function() {
        return layoutComputerUtils.getGridHeight(this.height, this.cellHeight);
      },
      // Determine has any full size layout item
      hasFullSizeItem: function() {
        return !!this.fullSizedItem;
      },
      // Determine whether an item is resizing
      isResizing: function() {
        return some('isResizing', this.layout);
      },
      // Determine whether an item is moving
      isMoving: function() {
        return some('isMoving', this.layout);
      },
      // Determine whether an item is interacting (moving or resizing)
      isInteracting: function() {
        return this.isResizing || this.isMoving;
      },
      // Full size layout item
      fullSizedItem: function() {
        return find(
          {
            isFullSize: true
          },
          this.layout
        );
      },
      // Max rows numbers that contains all items
      maxItemRows: function() {
        var cellHeight = this.cellHeight;
        var getBottom = function(item) {
          var pxBottom = item.pxTop + item.pxHeight;
          return layoutComputerUtils.getGridHeight(pxBottom, cellHeight);
        };
        return (
          placeholder(this.layout)
            .map(getBottom)
            .max() || 0
        );
      },
      // Max cols numbers that contains all items
      maxItemCols: function() {
        var cellWidth = this.cellWidth;
        var getRight = function(item) {
          var pxRight = item.pxLeft + item.pxWidth;
          return layoutComputerUtils.getGridWidth(pxRight, cellWidth);
        };
        return (
          placeholder(this.layout)
            .map(getRight)
            .max() || 0
        );
      }
    });
  }

  /**
   * Toggle snap edit state.
   */
  GridLayoutUIStore.prototype.setSnapToGrid = action(function setSnapToGrid(value) {
    this.isSnapEditOn = value;
  });

  /**
   * Set layout data whenever it changed for synching data & layout UI logic.
   */
  GridLayoutUIStore.prototype.setLayout = action(function setLayout(layoutData, initial) {
    var layoutStore = this;
    var sourceItems = layoutStore.layout;
    var updatedItems = layoutData || [];

    var updateSourceItem = function updateSourceItem(updatedItem) {
      // Find existing layout item
      var match = find(
        {
          id: updatedItem.id
        },
        sourceItems
      );

      var layoutItem = {
        id: updatedItem.id,
        isLoading: updatedItem.isLoading,
        isFullSize: updatedItem.isFullSize,
        autoSize: updatedItem.autoSize,
        gridLeft: updatedItem.x,
        gridTop: updatedItem.y,
        gridWidth: updatedItem.width < layoutStore.minItemWidth ? layoutStore.minItemWidth : updatedItem.width,
        gridHeight: updatedItem.height < layoutStore.minItemHeight ? layoutStore.minItemHeight : updatedItem.height
      };

      if (match) {
        match.copy(layoutItem);
      } else {
        // Add new layout item
        sourceItems.push(createLayoutItem(layoutItem));
      }
    };

    var removeItem = sourceItems.remove.bind(sourceItems);

    var deletedItems = filter(function(item) {
      return !some(['id', item.id], updatedItems);
    }, sourceItems);

    // Update existing or add new to sourceItems
    updatedItems.forEach(updateSourceItem);
    // Delete absent item in updatedItems
    deletedItems.forEach(removeItem);

    this.arrange(null);
  });

  /**
   * Update view width (px) of grid layout
   */
  GridLayoutUIStore.prototype.setViewWidth = action(function setViewWidth(width) {
    this.innerWidth = width;
  });
  /**
   * Set auto sizing of grid view
   */
  GridLayoutUIStore.prototype.setAutoSize = action(function setAutoSize(fullSize) {
    this.isFullSize = fullSize;
  });
  /**
   * Set Height only
   */
  GridLayoutUIStore.prototype.setFullHeightOnly = action(function setFullHeightOnly(fullHeightOnly) {
    this.isFullHeightOnly = fullHeightOnly;
  });
  /**
   * Set position of interacting overlay box. It will be periodically updated accordingly
   * with new position when user moving or resizing
   */
  GridLayoutUIStore.prototype.setOverlayPosition = action(function setOverlayPosition(newPos) {
    this.overlayPosition = newPos;
  });

  /**
   * Reset position of interacting overlay box. Should be called when moving/resizing has been completed.
   */
  GridLayoutUIStore.prototype.resetOverlayPosition = action(function resetOverlayPosition() {
    this.overlayPosition = {
      top: 0,
      left: 0,
      width: 0,
      height: 0
    };
  });

  /**
   * Calculate grid view height that respects auto size mode
   */
  GridLayoutUIStore.prototype.calcHeight = function() {
    /* Cases break down
      - (infinityRows: true, isFullSize: true)
          Take max value of (total rows of items + 1) and default rows number. So that it makes
          sure its min rows is default rows number in case of empty. See in dashboard
      - (infinityRows: false, isFullSize: true)
          Take max value of (total rows of item) and default rows number. See report viewer
          and format.
      - (infinityRows: true, isFullSize: false)
          Take default rows number. See report designer.
      - (infinityRows: false, isFullSize: false)
          Take default rows number. See report viewer in quick edit mode.
    */
    var scaleByRows = this.isFullHeightOnly || (this.infinityRows && this.isFullSize && !this.hasFullSizeItem);
    var maxRows = Math.max(this.maxItemRows, this.rows);
    var rows = this.isFullHeightOnly ? maxRows : this.infinityRows || !this.isFullSize ? this.rows : maxRows;

    var containingRows = scaleByRows ? this.maxItemRows + 1 : rows;
    containingRows = Math.max(containingRows, this.rows);

    return this.cellHeight * containingRows;
  };

  /**
   * Get layout item by its id
   * @param  {String} id Layout item id
   */
  GridLayoutUIStore.prototype.getLayoutItem = function(id) {
    return find(
      {
        id: id
      },
      this.layout
    );
  };

  /**
   * Get actual position (pixel) of a layout item by its id
   * @param  {String} id Layout item id
   */
  GridLayoutUIStore.prototype.getActualPositionById = function(id) {
    var itemState = this.getLayoutItem(id);
    return this.getActualPosition(itemState);
  };

  /**
   * Get actual position (pixel) of a layout item.
   * @param  {String} id Layout item id
   */
  GridLayoutUIStore.prototype.getActualPosition = function(layoutItem) {
    return layoutComputerUtils.getDomPosition(
      layoutItem.x || layoutItem.gridLeft || 0,
      layoutItem.y || layoutItem.gridTop || 0,
      layoutItem.width || layoutItem.gridWidth || 0,
      layoutItem.height || layoutItem.gridHeight || 0,
      this.cellWidth,
      this.cellHeight
    );
  };

  /**
   * Convert actual DOM position to grid view position
   * @param  {Object} position DOM's element position
   */
  GridLayoutUIStore.prototype.toGridPosition = function(domPosition) {
    return layoutComputerUtils.getGridPosition(
      domPosition.left,
      domPosition.top,
      domPosition.width,
      domPosition.height,
      this.cellWidth,
      this.cellHeight
    );
  };

  /**
   * Calculate new position (pixel) that snaps to grid layout
   * @param  {Obect} position Checking position
   */
  GridLayoutUIStore.prototype.snap = function(position) {
    var top = position.top;
    var left = position.left;
    var right = position.left + position.width;
    var bottom = position.top + position.height;
    var leftPadding = this.leftPadding || 0;

    var slackTop = top % this.cellHeight;
    var slackLeft = left % this.cellWidth;
    var slackRight = right % this.cellWidth;
    var slackBottom = bottom % this.cellHeight;

    var adjustTop =
      slackTop === 0 ? 0 : slackTop / this.cellHeight < SNAP_RATIO ? -slackTop : this.cellHeight - slackTop;
    var adjustLeft =
      slackLeft === 0 ? 0 : slackLeft / this.cellWidth < SNAP_RATIO ? -slackLeft : this.cellWidth - slackLeft;

    var adjustRight =
      slackRight === 0 ? 0 : slackRight / this.cellWidth < SNAP_RATIO ? -slackRight : this.cellWidth - slackRight;

    var adjustBottom =
      slackBottom === 0 ? 0 : slackBottom / this.cellHeight < SNAP_RATIO ? -slackBottom : this.cellHeight - slackBottom;

    var snapTop = top + adjustTop;
    var snapLeft = left + adjustLeft;
    var snapRight = right + adjustRight;
    var snapBottom = bottom + adjustBottom;

    return {
      top: snapTop + 1,
      left: snapLeft + leftPadding + 1,
      width: snapRight - snapLeft,
      height: snapBottom - snapTop
    };
  };

  /**
   * Detect new position of a report part that collides to any others parts
   * @param  {String} id       Report part id (layout item id)
   * @param  {Object} position New actual DOM position of report part
   * @return {Boolean}         Collided or not
   */
  GridLayoutUIStore.prototype.detectCollision = function(id, domPosition) {
    var gridPosition = layoutComputerUtils.getGridPosition(
      domPosition.left,
      domPosition.top,
      domPosition.width,
      domPosition.height,
      this.cellWidth,
      this.cellHeight
    );
    gridPosition.id = id;

    var itemToRect = function(item) {
      return layoutComputerUtils.convertToRect(item.id, item.left, item.top, item.width, item.height);
    };
    var checkingRect = itemToRect(gridPosition);

    return chain(this.layout)
      .reject(['id', id])
      .map(function(layoutItem) {
        return {
          id: layoutItem.id,
          top: layoutItem.gridTop,
          left: layoutItem.gridLeft,
          width: layoutItem.gridWidth,
          height: layoutItem.gridHeight
        };
      })
      .map(itemToRect)
      .some(layoutComputerUtils.collide(checkingRect))
      .value();
  };

  /**
   * Detect any report part is swappable with current moving one.
   * It returns the swappable report part position and its identity
   * @param  {String} id          Current report part id
   * @param  {Object} domPosition Current report part position
   * @return {Object}             Swappable report part position & id
   */
  GridLayoutUIStore.prototype.detectSwap = function(id, domPosition) {
    var gridPosition = layoutComputerUtils.getGridPosition(
      domPosition.left,
      domPosition.top,
      domPosition.width,
      domPosition.height,
      this.cellWidth,
      this.cellHeight
    );
    var itemToRect = function(item) {
      return layoutComputerUtils.convertToRect(item.id, item.left, item.top, item.width, item.height);
    };

    var checkingRect = itemToRect(gridPosition);

    var swappableRect = placeholder(this.layout)
      .reject(['id', id])
      .map(function(layoutItem) {
        return {
          id: layoutItem.id,
          top: layoutItem.gridTop,
          left: layoutItem.gridLeft,
          width: layoutItem.gridWidth,
          height: layoutItem.gridHeight
        };
      })
      .map(itemToRect)
      .find(layoutComputerUtils.swappable(checkingRect, SWAP_INTERSECT_RATIO));

    return swappableRect
      ? {
          id: swappableRect.id,
          left: swappableRect.x1,
          top: swappableRect.y1,
          width: swappableRect.x2 - swappableRect.x1,
          height: swappableRect.y2 - swappableRect.y1
        }
      : null;
  };

  /**
   * Calculate the size of grid that's able to contain all layout items.
   *
   * @return {Object} Actual grid size (width, height)
   */
  GridLayoutUIStore.prototype.calcActualGridSize = function() {
    var additionalCols = this.infinityCols ? DEFAULT_ADD_COLS : 0;
    var additionalRows = this.infinityRows ? DEFAULT_ADD_ROWS : 0;
    var overlayPosition = this.overlayPosition;
    var overlayRight = overlayPosition ? overlayPosition.left + overlayPosition.width : 0;
    var overlayBottom = overlayPosition ? overlayPosition.top + overlayPosition.height : 0;

    // Calculate total width/height of layout items.
    var itemsWidth = (this.maxItemCols + additionalCols) * this.cellWidth;
    var itemsHeight = (this.maxItemRows + additionalRows) * this.cellHeight;

    // Calculate the static size that is the widest size to contain all static items( not overlay item)
    // As having any full size item, the grid view shows only that full size item.
    // So that in that case, return the view size.
    var staticWidth = this.hasFullSizeItem ? this.width : Math.max(itemsWidth, this.width);
    var staticHeight = this.hasFullSizeItem ? this.height : Math.max(itemsHeight, this.height);

    var dynamicWidth = overlayRight + additionalCols * this.cellWidth;
    var dynamicHeight = overlayBottom + additionalRows * this.cellHeight;

    return {
      width: this.hasFullSizeItem ? this.width : Math.max(staticWidth, dynamicWidth),
      height: this.hasFullSizeItem ? this.height : Math.max(staticHeight, dynamicHeight)
    };
  };

  /**
   * Implement algorithm to smart positioning of a report part position.
   * In some scenarios, it affects to other part positions that will be included
   * in result.
   * @param  {String} id           Report part item id
   * @param  {Object} itemPosition Report part item position
   * @return {Array}               Array of changing report part positions
   */
  GridLayoutUIStore.prototype.rearrange = function(layoutItem, position, move) {
    var id = layoutItem.id;
    var orgPosition = this.getActualPositionById(id);
    // If collision occurs, restore original position. Otherwise, update
    // new position.
    var collided = this.detectCollision(id, position);
    var swappableGridItem, swappableGridPxPosition;
    var swappableGridPosition = this.allowSwap && move && this.detectSwap(id, position);
    var needUpdatePosition = !collided;

    var gridItemPosition = needUpdatePosition ? this.toGridPosition(position) : this.toGridPosition(orgPosition);

    if (!needUpdatePosition && !swappableGridPosition) {
      // Restore position for overlay.
      this.setOverlayPosition(orgPosition);
    } else if (swappableGridPosition) {
      swappableGridItem = this.getLayoutItem(swappableGridPosition.id);
      swappableGridPxPosition = swappableGridItem.getPxPosition();

      // Swap actual pixel positions between 2 layout item.
      swappableGridItem.updatePosition(orgPosition);
      layoutItem.updatePosition(swappableGridPxPosition);

      // swap its id if swappable.
      id = swappableGridItem.id;
      swappableGridPosition.id = layoutItem.id;
    } else {
      // Update current layout to new position
      layoutItem.updatePosition(position);
    }

    gridItemPosition.id = id;
    return swappableGridPosition ? [gridItemPosition, swappableGridPosition] : [gridItemPosition];
  };

  GridLayoutUIStore.prototype.addItemAt = function(x, y) {
    if (!this.infinityCols && x >= this.cols)
      return null;
    var occupiedRects = layoutComputerUtils.convertLayoutsToRects(this.layout, this.cellWidth, this.cellHeight);
    var offset = layoutComputerUtils.calculateSpareOffset(x, y, occupiedRects);

    // Ensure min item width, height when adding new item.
    // If offset width or height is smaller than min size, take the min size.
    return {
      top: offset.top,
      left: offset.left,
      width: Math.max(this.minItemWidth, offset.width),
      height: Math.max(this.minItemHeight, offset.height)
    };
  };

  GridLayoutUIStore.prototype.keepLastAdjustedLayout = action(function(compactedLayout) {
    this.compactedLayout = compactedLayout;
  });

  GridLayoutUIStore.prototype.getLastCompactedLayout = function() {
    var minItemWidth = this.minItemWidth;
    var minItemHeight = this.minItemHeight;
    var compactedLayout = placeholder(this.compactedLayout)
      .map(function(layout) {
        return {
          id: layout.id,
          top: layout.top || 0,
          left: layout.left || 0,
          width: layout.width || minItemWidth,
          height: layout.height || minItemHeight
        };
      })
      .value();
    this.keepLastAdjustedLayout([]);
    return compactedLayout || [];
  };

  GridLayoutUIStore.prototype.snapArrange = function(id, positionToMove, move) {
    var minItemWidth = this.minItemWidth;
    var minItemHeight = this.minItemHeight;

    var gridPosition = {
      top: layoutComputerUtils.getGridTop(positionToMove.top, this.cellHeight) || 0,
      left: layoutComputerUtils.getGridLeft(positionToMove.left, this.cellWidth) || 0,
      height: layoutComputerUtils.getGridHeight(positionToMove.height, this.cellHeight) || minItemHeight,
      width: layoutComputerUtils.getGridWidth(positionToMove.width, this.cellWidth) || minItemWidth
    };

    var cloneLayout = this.getLayout();
    var layoutItem = find(
      {
        id: id
      },
      cloneLayout
    );

    var adjustedLayout = SnapUtils.move(cloneLayout, layoutItem, gridPosition, true);
    var compactedLayout = SnapUtils.compact(adjustedLayout);

    // For fix case: The overlayItem is not fit to layoutItem when resizing.
    if (move) {
      this.setOverlayPosition(positionToMove);
    } else {
      var focusingLayout = find(
        {
          id: id
        },
        compactedLayout
      );
      var focusingPosition = layoutComputerUtils.getDomPosition(
        focusingLayout.left,
        focusingLayout.top,
        focusingLayout.width,
        focusingLayout.height,
        this.cellWidth,
        this.cellHeight
      );
      this.setOverlayPosition(focusingPosition);
    }

    // Keep data for update all items when interaction is stopped.
    this.keepLastAdjustedLayout(compactedLayout);
    this.updateLayoutPosition(compactedLayout);
  };

  GridLayoutUIStore.prototype.compactLayout = action(function compactLayout() {
    var cloneLayout = this.getLayout();
    var compactedLayout = SnapUtils.compact(cloneLayout);

    // Keep data for update all items after interact Stop.
    this.keepLastAdjustedLayout(compactedLayout);
    this.updateLayoutPosition(compactedLayout);
  });

  GridLayoutUIStore.prototype.updateLayoutItemSize = action(function updateLayoutItemSize(
    item,
    newDomWidth,
    newDomHeight
  ) {
    var isFirstRender = item.pxWidth === 0 && item.pxHeight === 0;

    var oldItem = isFirstRender
      ? null
      : assign(
          {
            gridTop: layoutComputerUtils.getGridTop(item.pxTop, this.cellHeight),
            gridLeft: layoutComputerUtils.getGridLeft(item.pxLeft, this.cellWidth),
            gridWidth: layoutComputerUtils.getGridWidth(item.pxWidth, this.cellWidth),
            gridHeight: layoutComputerUtils.getGridHeight(item.pxHeight, this.cellHeight)
          },
          item
        );

    item.updateActualSize(newDomWidth, newDomHeight);
    item.updateGridPosition({
      top: item.gridTop,
      left: item.gridLeft,
      width: layoutComputerUtils.getGridWidth(newDomWidth, this.cellWidth),
      height: layoutComputerUtils.getGridHeight(newDomHeight, this.cellHeight)
    });
    this.arrange(oldItem);
  });

  GridLayoutUIStore.prototype.arrange = action(function arrange(oldGridItem) {
    var cellWidth = this.cellWidth;
    var cellHeight = this.cellHeight;

    // Convert old grid item state to layout item
    var oldLayoutItem = oldGridItem
      ? layoutComputerUtils.convertToLayoutItem(oldGridItem, cellWidth, cellHeight)
      : null;

    // Convert grid layout items
    var compactLayout = this.getLayout(true);

    // Arrange layout
    var output = layoutComputerUtils.compactLayout(compactLayout, oldLayoutItem, this.resize);

    // Update layout
    this.updateLayoutPosition(output);
  });

  GridLayoutUIStore.prototype.updatePosition = action(function updatePosition(layoutItem, newPosition, move) {
    var snappedPosition = this.snap(newPosition);
    return this.rearrange(layoutItem, snappedPosition, move);
  });

  GridLayoutUIStore.prototype.getLayout = function(ignorePx) {
    var cellWidth = this.cellWidth;
    var cellHeight = this.cellHeight;

    return placeholder(this.layout)
      .map(function(gridItem) {
        return layoutComputerUtils.convertToLayoutItem(gridItem, cellWidth, cellHeight, ignorePx);
      })
      .sortBy(['left', 'top'])
      .value();
  };
 
  // GridLayoutUIStore.prototype.updateLayoutPosition = action(function updateLayoutPosition(updates) {
  //   var cellWidth = this.cellWidth;
  //   var cellHeight = this.cellHeight;
  //   const { MOBILE_WIDTH, MOBILE_GRID_ITEM_WIDTH, MOBILE_GRID_ITEM_HEIGHT } = UIConstants;
  //   const isMobile = window.innerWidth <= MOBILE_WIDTH;
  //   const indexUpdates = function(updates) {
  //   const sortedLayout = sortBy(['top', 'left'], updates);
  //   sortedLayout.forEach((item, index) => item.index = index);
  //   };

  //   placeholder(this.layout).forEach(function(item) {
  //     var updated = placeholder(updates).find(['id', item.id]);
  //     if (updated) {
  //       // item.updatePosition({
  //       //   top: layoutComputerUtils.getDomTop(updated.top, cellHeight),
  //       //   left: layoutComputerUtils.getDomLeft(updated.left, cellWidth),
  //       //   width: layoutComputerUtils.getDomWidth(updated.width, cellWidth),
  //       //   height: layoutComputerUtils.getDomHeight(updated.height, cellHeight)
  //       // });
  //       if (isMobile) {
  //         item.updatePosition({
  //           top: layoutComputerUtils.getDomTop(updated.index * MOBILE_GRID_ITEM_HEIGHT, cellHeight),
  //           left: layoutComputerUtils.getDomLeft(0, cellWidth),
  //           width: layoutComputerUtils.getDomWidth(MOBILE_GRID_ITEM_WIDTH, cellWidth),
  //           height: layoutComputerUtils.getDomHeight(MOBILE_GRID_ITEM_HEIGHT, cellHeight)
  //         });
  //         console.log(item);
  //       } else {
  //         item.updatePosition({
  //           top: layoutComputerUtils.getDomTop(0, cellHeight),
  //           left: layoutComputerUtils.getDomLeft(0, cellWidth),
  //           width: layoutComputerUtils.getDomWidth(updated.width, cellWidth),
  //           height: layoutComputerUtils.getDomHeight(updated.height, cellHeight)
  //         });
  //       }
  //       item.updateGridPosition(updated);
  //     }
  //   });
  // });
     
  //New function to control reports in grid

  GridLayoutUIStore.prototype.updateLayoutPosition = action(function updateLayoutPosition(updates) {
    var leftPadding = this.leftPadding || 0;
    const { MOBILE_WIDTH, MOBILE_GRID_ITEM_WIDTH, MOBILE_GRID_ITEM_HEIGHT } = UIConstants;
    const isMobile = window.innerWidth <= MOBILE_WIDTH;
    const indexUpdates = function(updates) {
      const sortedLayout = sortBy(['top', 'left'], updates);
      sortedLayout.forEach((item, index) => item.index = index);
    };
   var cellWidth = this.cellWidth;
   var cellHeight = this.cellHeight;
   indexUpdates(updates);

   placeholder(this.layout).forEach(function(item) {
     var updated = placeholder(updates).find(['id', item.id]);
     if (updated) {
      //  if (window.outerWidth <= 1024) {
      //    item.updatePosition({
      //      top: layoutComputerUtils.getDomTop(0, cellHeight),
      //      left: layoutComputerUtils.getDomLeft(0, cellWidth),
      //      width: layoutComputerUtils.getDomWidth(updated.width, cellWidth),
      //      height: layoutComputerUtils.getDomHeight(updated.height, cellHeight)
      //    });
      //  } else {
      //    item.updatePosition({
      //      top: layoutComputerUtils.getDomTop(updated.top, cellHeight),
      //      left: layoutComputerUtils.getDomLeft(updated.left, cellWidth),
      //      width: layoutComputerUtils.getDomWidth(updated.width, cellWidth),
      //      height: layoutComputerUtils.getDomHeight(updated.height, cellHeight)
      //    });
      //  }
      if (isMobile) {
        item.updatePosition({
          top: layoutComputerUtils.getDomTop(updated.index * MOBILE_GRID_ITEM_HEIGHT, cellHeight),
          left: layoutComputerUtils.getDomLeft(0, cellWidth),
          width: layoutComputerUtils.getDomWidth(MOBILE_GRID_ITEM_WIDTH, cellWidth),
          height: layoutComputerUtils.getDomHeight(MOBILE_GRID_ITEM_HEIGHT, cellHeight)
        });
      } else {
        item.updatePosition({
          top: layoutComputerUtils.getDomTop(updated.top, cellHeight),
          left: layoutComputerUtils.getDomLeft(updated.left, cellWidth) + leftPadding,
          width: layoutComputerUtils.getDomWidth(updated.width, cellWidth),
          height: layoutComputerUtils.getDomHeight(updated.height, cellHeight)
        });
      }
       item.updateGridPosition(updated);
     }
   });
 });

  GridLayoutUIStore.prototype.recalculateLayoutPosition = function() {
    var compactLayout = this.getLayout(true);

    // Update layout
    this.updateLayoutPosition(compactLayout);
  };

  /**
   * Create an instance of GridItemState
   */
  function createLayoutItem(layoutItem) {
    return new GridItemState(
      layoutItem.id,
      layoutItem.gridLeft,
      layoutItem.gridTop,
      layoutItem.gridWidth,
      layoutItem.gridHeight,
      layoutItem.isFullSize,
      layoutItem.autoSize,
      layoutItem.isLoading
    );
  }

  module.exports = GridLayoutUIStore;
})();



// WEBPACK FOOTER //
// ./ui/share/components/controls/GridLayout/Izenda.GridLayoutUIStore.js